// New chat and the invite dialog (17-people.md, "New chat", "Invite dialog"), both on the shared
// people picker.
import type { MatrixClient, Room } from 'matrix-js-sdk';
import { useEffect, useState } from 'react';
import { Button } from '../blocks/Button';
import { Dialog, DialogButtons } from '../blocks/Dialog';
import { describe } from '../core/errors';
import { navigate } from '../core/router';
import { t } from '../i18n';
import { findDirectChat, openDirectChat } from './direct';
import { Picker } from './Picker';

/** /direct/create/ (with ?userId= offered first; an existing chat with them opens at once). */
export function NewChat({ client, onState }: { client: MatrixClient; onState?: (state: { busy: boolean; touched: boolean }) => void }) {
  const first = new URLSearchParams(location.search).get('userId') ?? undefined;
  const [error, setError] = useState<string>();
  useEffect(() => {
    const existing = first && findDirectChat(client, first);
    if (existing) navigate(`/direct/${encodeURIComponent(existing)}/`, { replace: true });
  }, [client, first]);
  return (
    <div className="hero picker-page">
      <h2>{t('frame.writeTo')}</h2>
      <Picker
        client={client}
        question={t('frame.writeTo')}
        first={first}
        withColleagues
        error={error}
        onState={onState}
        notInChatLine={(name) => t('people.notInChatMessage', { name })}
        onPick={(p) => openDirectChat(client, p.userId!, p.name).then(() => setError(undefined), (e: Error) => setError(e.message))}
      />
    </div>
  );
}

let openInvite: ((room: Room) => void) | undefined;
/** Opens the invite dialog for a channel or space (from its menus and the timeline's start). */
export const inviteTo = (room: Room) => openInvite?.(room);
export const mayInvite = (client: MatrixClient, room: Room) => room.canInvite(client.getUserId()!);

export function InviteHost({ client }: { client: MatrixClient }) {
  const [room, setRoom] = useState<Room>();
  useEffect(() => {
    openInvite = setRoom;
    return () => void (openInvite = undefined);
  }, []);
  return room ? <InviteDialog client={client} room={room} onClose={() => setRoom(undefined)} /> : null;
}

function InviteDialog({ client, room, onClose }: { client: MatrixClient; room: Room; onClose: () => void }) {
  const [error, setError] = useState<string>();
  const [invited, setInvited] = useState(() => new Set(room.getMembersWithMembership('invite').map((m) => m.userId)));
  const space = room.isSpaceRoom();
  return (
    <Dialog title={t('people.inviteTo', { name: room.name })} onClose={onClose} className="invite-dialog">
      <Picker
        client={client}
        question={t('people.whoInvite')}
        withColleagues
        error={error}
        leaveOut={(id) => room.getMember(id)?.membership === 'join'}
        tagOf={(id) => (invited.has(id) ? t('people.invited') : undefined)}
        notInChatLine={(name) => t('people.notInChatInvite', { name })}
        onPick={async (p) => {
          try {
            await client.invite(room.roomId, p.userId!);
            setInvited((s) => new Set(s).add(p.userId!));
            setError(undefined);
          } catch (e) {
            setError(describe(e, 'general').kind === 'FB' ? t(space ? 'people.cantInviteSpace' : 'people.cantInvite') : t('people.inviteFailed', { name: p.name }));
          }
        }}
      />
      <DialogButtons>
        <Button variant="text" onClick={onClose}>
          {t('people.done')}
        </Button>
      </DialogButtons>
    </Dialog>
  );
}
